import { ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useGoogleAnalytics } from '@/services/analytics';
import { useAppConfigStore } from '@stores/appConfig.store';

const DEFAULT_COOKIE_CONFIG = {
  necessaryCookies: true,
  analyticsCookies: false,
  functionalityOrPersonalizationCookies: false,
};

const ALL_COOKIES_ENABLED = {
  ...DEFAULT_COOKIE_CONFIG,
  analyticsCookies: true,
  functionalityOrPersonalizationCookies: true,
};

export const useCookiesStore = defineStore('cookiesStore', () => {
  const appConfigStore = useAppConfigStore();
  const { applicationSetup, setupLoaded } = storeToRefs(appConfigStore);
  const userCookies = ref({ ...DEFAULT_COOKIE_CONFIG });

  const getCookieKey = () => {
    return `userCookies${applicationSetup.value.hotel_id}`;
  };

  const getConsentKey = () => {
    return `consentCookies${applicationSetup.value.hotel_id}`;
  };

  const initializeCookies = () => {
    if (!setupLoaded.value || !applicationSetup.value?.hotel_id) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const ckParam = urlParams.get('ck');
    const cookieKey = getCookieKey();
    const storedCookies = localStorage.getItem(cookieKey);

    if (ckParam) {
      const activatedCookies = ckParam.split('');
      userCookies.value = {
        ...DEFAULT_COOKIE_CONFIG,
        analyticsCookies: activatedCookies[1] === '1',
        functionalityOrPersonalizationCookies: activatedCookies[2] === '1',
      };
      saveCookiesConfiguration();
      return;
    }

    if (storedCookies) {
      userCookies.value = JSON.parse(storedCookies);
    }
  };

  const acceptAllCookies = () => {
    userCookies.value = { ...ALL_COOKIES_ENABLED };
    saveCookiesConfiguration();
  };

  const rejectAllCookies = () => {
    userCookies.value = { ...DEFAULT_COOKIE_CONFIG };
    deleteCookies(['loyalty_user', 'active_profile', 'social_media_user']);
    saveCookiesConfiguration();
  };

  const confirmPreferences = (cookies) => {
    userCookies.value = cookies;
    deleteCookies(['loyalty_user', 'active_profile', 'social_media_user']);
    saveCookiesConfiguration();
  };

  const saveCookiesConfiguration = () => {
    const consentKey = getConsentKey();
    const cookieKey = getCookieKey();
    if (!consentKey || !cookieKey) return;

    localStorage.setItem(consentKey, 'true');
    localStorage.setItem(cookieKey, JSON.stringify(userCookies.value));
  };

  const setCookie = (name, value) => {
    if (!userCookies.value?.functionalityOrPersonalizationCookies) return;

    const oneMonthFromNow = new Date();
    oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
    document.cookie = `${name}=${JSON.stringify(value)}; path=/; expires=${oneMonthFromNow.toUTCString()};`;
  };

  const deleteCookies = (names) => {
    const expiredDate = new Date(0).toUTCString();
    names.forEach((name) => {
      document.cookie = `${name}=; path=/; expires=${expiredDate};`;
    });
  };

  const deleteCookie = (name) => deleteCookies([name]);

  watch(
    () => setupLoaded.value,
    (loaded) => {
      if (loaded) {
        initializeCookies();
      }
    },
    { immediate: true },
  );

  watch(
    () => applicationSetup.value?.be_cookies,
    (newValue) => {
      if (!newValue) {
        acceptAllCookies();
        localStorage.removeItem(getConsentKey());
      } else if (!localStorage.getItem(getConsentKey())) {
        userCookies.value = { ...DEFAULT_COOKIE_CONFIG };
        localStorage.removeItem(getCookieKey());
      }
    },
  );

  watch(
    () => userCookies.value.analyticsCookies,
    (newValue) => {
      if (!applicationSetup.value.gaCodes) return;

      const { initializeAnalytics, stopAnalytics } = useGoogleAnalytics();

      // Always remove existing analytics first
      //stopAnalytics();

      // Initialize only if consent is given
      if (newValue) {
        initializeAnalytics(applicationSetup.value.gaCodes);
      } else {
        stopAnalytics();
      }
    },
    { immediate: true },
  );

  return {
    userCookies,
    acceptAllCookies,
    rejectAllCookies,
    confirmPreferences,
    setCookie,
    deleteCookie,
  };
});
